import { useSyncExternalStore } from 'react';

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function detectInstallPlatform(userAgent = navigator.userAgent, platform = navigator.platform, touchPoints = navigator.maxTouchPoints) {
  if (/iPhone|iPad|iPod/i.test(userAgent) || (platform === 'MacIntel' && touchPoints > 1)) return 'ios';
  if (/Android/i.test(userAgent)) return 'android';
  return 'desktop';
}

export function isInAppBrowser(userAgent = navigator.userAgent) {
  return /KAKAOTALK|Instagram|FBAN|FBAV|\bLine\/|; wv\)/i.test(userAgent);
}

function standalone() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

// Listen before routes mount so visiting login/settings cannot consume or lose the prompt.
let state = { installed: standalone(), installing: false, prompt: null as InstallPromptEvent | null };
const listeners = new Set<() => void>();
function update(next: Partial<typeof state>) {
  state = { ...state, ...next };
  listeners.forEach(listener => listener());
}
window.addEventListener('beforeinstallprompt', (event) => {
  event.preventDefault();
  update({ prompt: event as InstallPromptEvent });
});
window.addEventListener('appinstalled', () => update({ installed: true, prompt: null, installing: false }));
window.matchMedia('(display-mode: standalone)').addEventListener('change', () => update({ installed: standalone() }));

export async function requestAppInstall(): Promise<'accepted' | 'dismissed' | 'unavailable' | 'failed'> {
  const prompt = state.prompt;
  if (!prompt || state.installing) return 'unavailable';
  update({ installing: true, prompt: null });
  try {
    await prompt.prompt();
    return (await prompt.userChoice).outcome;
  } catch {
    return 'failed';
  } finally {
    update({ installing: false });
  }
}

export function useAppInstall() {
  return useSyncExternalStore(
    (listener) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    () => state,
  );
}
